import React from 'react';
import Breadcrumb from '../../components/breadcrumb';
import { BREADCRUMB_ROOT } from '../../common/config';
import UQButton from '../../components/button';
import UQGrid from '../../components/grid';
import UQCard from '../../components/card';
import UQSpacer from '../../components/spacer';
import UQCode from '../../components/code';
import UQDataGrid, { UQDataGridColumn } from '../../components/data-grid';

const ButtonPage = () => {
  const colors = [
    'blue',
    'turquois',
    'mint',
    'green',
    'yellow',
    'peach',
    'salmon',
    'purple',
  ];

  const attributes = [
    {
      attribute: 'type',
      type: "'primary' | 'secondary' | 'action'",
      default: "'primary'",
      description: 'The style of the button.',
    },
    {
      attribute: 'size',
      type: "'small' | 'large'",
      default: 'undefined',
      description: 'The size of the button.',
    },
    {
      attribute: 'color',
      type: 'string',
      default: 'undefined',
      description: 'The color of the button.',
    },
    {
      attribute: 'icon',
      type: 'string',
      default: 'undefined',
      description: 'The icon to display inside the button.',
    },
    {
      attribute: 'iconPosition',
      type: "'left' | 'right'",
      default: "'left'",
      description: 'The position of the icon relative to the text.',
    },
    {
      attribute: 'disabled',
      type: 'boolean',
      default: 'false',
      description: 'Whether the button is disabled.',
    },
  ];

  const attributeColumns: UQDataGridColumn<typeof attributes[0]>[] = [
    {
      columnId: 'attribute',
      renderHeaderCell: () => 'Attribute',
      renderCell: (item) => item.attribute,
    },
    {
      columnId: 'type',
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <code>{item.type}</code>,
    },
    {
      columnId: 'default',
      renderHeaderCell: () => 'Default',
      renderCell: (item) => <code>{item.default}</code>,
    },
    {
      columnId: 'description',
      renderHeaderCell: () => 'Description',
      renderCell: (item) => item.description,
    },
  ];

  return (
    <>
      <Breadcrumb crumbs={[BREADCRUMB_ROOT, 'Button']} />
      <h1>UQButton</h1>
      <UQSpacer size="m" />

      {/* Description */}
      <h2>Description</h2>
      <p>
        The <code>UQButton</code> component is a versatile button that supports various styles, sizes, and colors. It can also include icons on the left or right side of the button text.
      </p>
      <UQSpacer size="m" />

      {/* Attributes Table */}
      <h2>Attributes</h2>
      <UQSpacer size="s" />
      <UQDataGrid
        items={attributes}
        columns={attributeColumns}
        sortable={false}
        selectionMode="none"
      />
      <UQSpacer size="m" />

      {/* Button Examples */}
      <h2>Examples</h2>
      <UQSpacer size="s" />
      <UQGrid>
        {['primary', 'secondary', 'action'].map((type) => (
          <UQCard key={type}>
            <h2>{type.charAt(0).toUpperCase() + type.slice(1)} Buttons</h2>
            <UQSpacer size="m" />

            {/* Default Button */}
            <h3>Default</h3>
            <UQSpacer size="s" />
            <UQGrid justify="space-between">
              <UQButton type={type as 'primary' | 'secondary' | 'action'}>
                default
              </UQButton>
            </UQGrid>
            <UQSpacer size="m" />

            {/* Small Buttons */}
            <h3>Small</h3>
            <UQSpacer size="s" />
            <UQGrid justify="space-between">
              {colors.map((color) => (
                <UQButton
                  key={`small-${color}`}
                  type={type as 'primary' | 'secondary' | 'action'}
                  size="small"
                  color={color}
                >
                  {color}
                </UQButton>
              ))}
            </UQGrid>
            <UQSpacer size="m" />

            {/* Large Buttons */}
            <h3>Large</h3>
            <UQSpacer size="s" />
            <UQGrid justify="space-between">
              {colors.map((color) => (
                <UQButton
                  key={`large-${color}`}
                  type={type as 'primary' | 'secondary' | 'action'}
                  size="large"
                  color={color}
                >
                  {color}
                </UQButton>
              ))}
            </UQGrid>
            <UQSpacer size="m" />

            {/* Buttons with Icons */}
            <h3>Buttons with Icons</h3>
            <UQSpacer size="s" />
            <UQGrid justify="space-between">
              <UQButton
                type={type as 'primary' | 'secondary' | 'action'}
                icon="check"
                iconPosition="left"
              >
                Icon Left
              </UQButton>
              <UQButton
                type={type as 'primary' | 'secondary' | 'action'}
                icon="arrow-right"
                iconPosition="right"
              >
                Icon Right
              </UQButton>
            </UQGrid>
            <UQSpacer size="m" />

            {/* Code Snippet */}
            <UQCode
              code={`import React from 'react';\nimport { UQButton, UQGrid } from '@uniqconsulting/ui';\n\n<UQGrid justify="space-between">\n\t<UQButton type="${type}">default</UQButton>\n` +
                colors
                  .map(
                    (color) =>
                      `\t<UQButton type="${type}" size="small" color="${color}">${color}</UQButton>`
                  )
                  .join('\n') +
                '\n' +
                colors
                  .map(
                    (color) =>
                      `\t<UQButton type="${type}" size="large" color="${color}">${color}</UQButton>`
                  )
                  .join('\n') +
                `\n\t<UQButton type="${type}" icon="check" iconPosition="left">Icon Left</UQButton>\n` +
                `\t<UQButton type="${type}" icon="arrow-right" iconPosition="right">Icon Right</UQButton>\n` +
                '\n</UQGrid>'}
              enableCopy
            />
          </UQCard>
        ))}
      </UQGrid>
    </>
  );
};

export default ButtonPage;
